import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ChevronDown, ChevronRight, Search, Eye } from 'lucide-react';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ACCOUNT_TYPES, groupAccountsBySubType } from '../../utils/glAccountTypes';
import ReportIssueButton from '../report/ReportIssueButton';

const TrialBalanceReport = ({
  reportData,
  viewMode,
  formatAmount,
  showGlCodes,
  showZeroBalances,
  setShowZeroBalances,
  trialBalanceFilter,
  setTrialBalanceFilter,
  openDrilldown
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [collapsedTypes, setCollapsedTypes] = React.useState({});
  const [collapsedSubGroups, setCollapsedSubGroups] = React.useState({});

  const toggleType = (type) => setCollapsedTypes(prev => ({ ...prev, [type]: !prev[type] }));
  const toggleSubGroup = (key) => setCollapsedSubGroups(prev => ({ ...prev, [key]: !prev[key] }));

  // Filter accounts first
  const filteredAccounts = React.useMemo(() => {
    const accounts = reportData?.accounts || reportData?.items || [];
    return accounts.filter(acc => {
      const matchesSearch = !searchQuery ||
        acc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        acc.code?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesZeroFilter = showZeroBalances || (acc.debit !== 0 || acc.credit !== 0);
      const matchesTypeFilter = trialBalanceFilter === 'all' || acc.type === trialBalanceFilter;
      return matchesSearch && matchesZeroFilter && matchesTypeFilter;
    });
  }, [reportData, searchQuery, showZeroBalances, trialBalanceFilter]);

  // Group by type -> sub-type
  const grouped = React.useMemo(() => groupAccountsBySubType(filteredAccounts), [filteredAccounts]);

  // Calculate totals
  const totals = React.useMemo(() => {
    return filteredAccounts.reduce((sum, acc) => ({
      debit: sum.debit + (acc.debit || 0),
      credit: sum.credit + (acc.credit || 0)
    }), { debit: 0, credit: 0 });
  }, [filteredAccounts]);

  const isBalanced = Math.abs(totals.debit - totals.credit) < 0.01;

  if (!reportData) return null;

  // Determine which types to show (either all or just the filtered one)
  const typesToShow = trialBalanceFilter === 'all'
    ? ACCOUNT_TYPES
    : ACCOUNT_TYPES.filter(t => t.value === trialBalanceFilter);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card data-pdf-hide="">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search accounts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={trialBalanceFilter} onValueChange={setTrialBalanceFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="asset">Assets</SelectItem>
                <SelectItem value="liability">Liabilities</SelectItem>
                <SelectItem value="equity">Equity</SelectItem>
                <SelectItem value="revenue">Revenue</SelectItem>
                <SelectItem value="expense">Expenses</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={showZeroBalances}
                onChange={(e) => setShowZeroBalances(e.target.checked)}
                className="rounded"
              />
              Show zero balances
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Balance Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Badge variant={isBalanced ? 'default' : 'destructive'} className="text-sm">
            {isBalanced ? 'Trial Balance is Balanced' : 'Trial Balance Out of Balance'}
          </Badge>
          <span className="text-sm text-muted-foreground">{filteredAccounts.length} accounts</span>
        </div>
        {!isBalanced && trialBalanceFilter === 'all' && !searchQuery && (
          <ReportIssueButton
            label="This shouldn't happen - report it"
            testId="tb-out-of-balance-report-btn"
            fingerprint="trial_balance_out_of_balance"
            issueContext={{
              trigger: 'trial_balance_out_of_balance',
              total_debits: totals.debit,
              total_credits: totals.credit,
              difference: totals.debit - totals.credit,
            }}
          />
        )}
      </div>

      {/* Summary View */}
      {viewMode === 'summary' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-blue-50/50 border-blue-200">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground mb-1">Total Debits</p>
              <p className="text-base md:text-lg font-bold text-blue-600 break-all">{formatAmount(totals.debit)}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50/50 border-green-200">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground mb-1">Total Credits</p>
              <p className="text-base md:text-lg font-bold text-green-600 break-all">{formatAmount(totals.credit)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detailed Table with Type -> Sub-Type Grouping */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                {showGlCodes && <TableHead className="w-[100px]">GL Code</TableHead>}
                <TableHead>Account Name</TableHead>
                <TableHead className="text-right w-[150px]">Debit</TableHead>
                <TableHead className="text-right w-[150px]">Credit</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {typesToShow.map(({ value: typeKey, label: typeLabel, color }) => {
                const typeData = grouped[typeKey];
                if (!typeData || typeData.totalAccounts === 0) return null;
                const isTypeCollapsed = collapsedTypes[typeKey];

                // Calculate type totals
                const typeAccounts = typeData.subGroups.flatMap(sg => sg.accounts);
                const typeDebit = typeAccounts.reduce((s, a) => s + (a.debit || 0), 0);
                const typeCredit = typeAccounts.reduce((s, a) => s + (a.credit || 0), 0);

                return (
                  <React.Fragment key={typeKey}>
                    {/* Type Header Row */}
                    <TableRow
                      className="bg-muted/50 cursor-pointer hover:bg-muted/70"
                      onClick={() => toggleType(typeKey)}
                    >
                      <TableCell colSpan={showGlCodes ? 5 : 4}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 font-semibold">
                            {isTypeCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            <Badge className={color}>{typeLabel}</Badge>
                            <span className="text-xs text-muted-foreground">({typeData.totalAccounts})</span>
                          </div>
                          <div className="flex items-center gap-6 text-sm font-medium">
                            <span className="text-blue-600">{typeDebit > 0 ? formatAmount(typeDebit) : '-'}</span>
                            <span className="text-green-600 mr-8">{typeCredit > 0 ? formatAmount(typeCredit) : '-'}</span>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Sub-Groups */}
                    {!isTypeCollapsed && typeData.subGroups.map(({ prefix, label: subLabel, accounts }) => {
                      const sgKey = `${typeKey}-${prefix}`;
                      const isSgCollapsed = collapsedSubGroups[sgKey];
                      const sgDebit = accounts.reduce((s, a) => s + (a.debit || 0), 0);
                      const sgCredit = accounts.reduce((s, a) => s + (a.credit || 0), 0);

                      return (
                        <React.Fragment key={sgKey}>
                          {/* Sub-Group Header */}
                          <TableRow
                            className="bg-muted/20 cursor-pointer hover:bg-muted/40"
                            onClick={() => toggleSubGroup(sgKey)}
                          >
                            <TableCell colSpan={showGlCodes ? 5 : 4}>
                              <div className="flex items-center justify-between pl-4">
                                <div className="flex items-center gap-2 text-sm">
                                  {isSgCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  <span className="font-medium">{subLabel}</span>
                                  <span className="font-mono text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{prefix}XX</span>
                                  <span className="text-xs text-muted-foreground">({accounts.length})</span>
                                </div>
                                <div className="flex items-center gap-6 text-xs font-medium">
                                  <span className="text-blue-600">{sgDebit > 0 ? formatAmount(sgDebit) : '-'}</span>
                                  <span className="text-green-600 mr-8">{sgCredit > 0 ? formatAmount(sgCredit) : '-'}</span>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>

                          {/* Account Rows */}
                          {!isSgCollapsed && accounts.map((acc) => (
                            <TableRow
                              key={acc.code}
                              className="hover:bg-muted/20 cursor-pointer"
                              onClick={() => openDrilldown(acc)}
                            >
                              {showGlCodes && (
                                <TableCell className="font-mono text-sm text-muted-foreground pl-10">{acc.code}</TableCell>
                              )}
                              <TableCell className={showGlCodes ? '' : 'pl-10'}>{acc.name}</TableCell>
                              <TableCell className="text-right font-medium">
                                {acc.debit > 0 ? formatAmount(acc.debit) : '-'}
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {acc.credit > 0 ? formatAmount(acc.credit) : '-'}
                              </TableCell>
                              <TableCell>
                                <Eye className="w-4 h-4 text-muted-foreground" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}

              {/* Totals Row */}
              <TableRow className="bg-muted font-bold border-t-2">
                {showGlCodes && <TableCell></TableCell>}
                <TableCell>Total</TableCell>
                <TableCell className="text-right text-blue-600">{formatAmount(totals.debit)}</TableCell>
                <TableCell className="text-right text-green-600">{formatAmount(totals.credit)}</TableCell>
                <TableCell></TableCell>
              </TableRow>

              {!isBalanced && (
                <TableRow className="bg-red-50">
                  {showGlCodes && <TableCell></TableCell>}
                  <TableCell className="text-red-600 font-medium">Difference</TableCell>
                  <TableCell colSpan={2} className="text-right text-red-600 font-bold">
                    {formatAmount(Math.abs(totals.debit - totals.credit))}
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default TrialBalanceReport;
